/**
 * Hook for updating position TP/SL via backend API
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { API_BASE_URL } from "@/backend/config";

interface UpdateTPSLRequest {
  user_id: string;
  market: string;
  take_profit?: number | null;
  stop_loss?: number | null;
}

/**
 * Update TP/SL for a position
 */
const updatePositionTPSL = async (request: UpdateTPSLRequest): Promise<void> => {
  // Use fetch directly for PUT request — import is at module level
  const response = await fetch(`${API_BASE_URL}/api/positions/${request.market}/tpsl`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: request.user_id,
      take_profit: request.take_profit,
      stop_loss: request.stop_loss,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to update TP/SL" }));
    throw new Error(error.message || "Failed to update TP/SL");
  }
};

export const useUpdateTPSL = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePositionTPSL,
    onSuccess: () => {
      // Success toast
      toast.success("TP/SL updated successfully");

      // Invalidate queries to refetch position data
      queryClient.invalidateQueries({ queryKey: ["backend-positions", userId] });
      queryClient.invalidateQueries({ queryKey: ["account-sync", userId] });
    },
    onError: (error: Error) => {
      // Error toast
      toast.error(error.message || "Failed to update TP/SL");
      
      console.error("[useUpdateTPSL] Error:", error);
    },
  });
};

